/**
 * Wave 4 P8 / P9 game settings (docs/GAME.md#wonders): the `wonders` feature toggle and the
 * extra victory conditions. Pure (only core/types): shared by the simulation, the settings
 * validation (src/shared/validate.ts), the client's saved settings and the setup form.
 *
 * "Win by land, wonders, the centre or gold." Territory, last standing and the time limit always
 * apply; wonder victory, king of the hill and economic victory are OFF unless switched on.
 */
import { featureOn, type GameSettings } from "../types";

/** Ranges and defaults of the victory settings (minutes / wonders / gold). */
export const VICTORY_LIMITS = {
  /** 0 = a majority of the map's wonders. */
  wonderVictoryCount: { min: 0, max: 8, def: 0 },
  wonderVictoryMinutes: { min: 1, max: 30, def: 5 },
  /**
   * Stage N (final rebalance): default 5 -> 10. With 5 minutes the nation that spawned on the centre
   * won every King of the Hill game after 5.6-8.2 minutes (Simple 10-20, Full 20-40 minutes).
   */
  hillVictoryMinutes: { min: 1, max: 30, def: 10 },
  economicVictoryGold: { min: 1_000_000, max: 1_000_000_000, def: 25_000_000 },
} as const;

/**
 * Stage O (module simplify, owner-approved P9): the three gold targets of the Economic mode offered
 * by the setup (short / normal / long) instead of nine steps. Any value in VICTORY_LIMITS still
 * plays (replays, lobbies); remembered setups snap to the nearest target (nearestEconomicGold).
 */
export const ECONOMIC_GOLD_TARGETS = { short: 10_000_000, normal: VICTORY_LIMITS.economicVictoryGold.def, long: 100_000_000 } as const;
export type EconomicGoldTarget = keyof typeof ECONOMIC_GOLD_TARGETS;
export const ECONOMIC_GOLD_TARGET_IDS: readonly EconomicGoldTarget[] = ["short", "normal", "long"];

/** The target closest to `gold` (ties go to the shorter one; not a number = normal). */
export function nearestEconomicTarget(gold: unknown): EconomicGoldTarget {
  if (typeof gold !== "number" || !Number.isFinite(gold)) return "normal";
  let best: EconomicGoldTarget = "short";
  for (const id of ECONOMIC_GOLD_TARGET_IDS) {
    if (Math.abs(ECONOMIC_GOLD_TARGETS[id] - gold) < Math.abs(ECONOMIC_GOLD_TARGETS[best] - gold)) best = id;
  }
  return best;
}

/** The gold of the target closest to `gold`. */
export function nearestEconomicGold(gold: unknown): number {
  return ECONOMIC_GOLD_TARGETS[nearestEconomicTarget(gold)];
}

/** Game settings keys owned by this module (validated explicitly, not passed through as extras). */
export const VICTORY_SETTING_KEYS: readonly (keyof GameSettings)[] = [
  "wonders",
  "wonderVictory",
  "wonderVictoryCount",
  "wonderVictoryMinutes",
  "hillVictory",
  "hillVictoryMinutes",
  "economicVictory",
  "economicVictoryGold",
];

/** Effective victory / wonder settings of a game (defaults filled in, numbers clamped). */
export interface VictorySettings {
  wonders: boolean;
  wonderVictory: boolean;
  /** 0 = majority of the placed wonders. */
  wonderCount: number;
  wonderMinutes: number;
  hillVictory: boolean;
  hillMinutes: number;
  economicVictory: boolean;
  economicGold: number;
}

function clampInt(v: unknown, lo: number, hi: number, def: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return def;
  const r = Math.round(v);
  return r < lo ? lo : r > hi ? hi : r;
}

/**
 * Read the effective victory settings (absent fields → defaults; wonder victory needs wonders).
 * Stage J (module modes): with a game mode set, the mode picks the condition (Wonder Race, King of
 * the Hill, Economic; Conquest and Rising Sea have none of them) and the ruleset decides `wonders`;
 * settings made before stage J use their stored switches.
 */
export function victorySettingsOf(s: GameSettings): VictorySettings {
  const L = VICTORY_LIMITS;
  const wonders = featureOn(s, "wonders");
  const m = s.winMode;
  const on = (stored: boolean | undefined, mode: string): boolean => (m === undefined ? stored === true : m === mode);
  return {
    wonders,
    wonderVictory: wonders && on(s.wonderVictory, "wonderRace"),
    wonderCount: clampInt(s.wonderVictoryCount, L.wonderVictoryCount.min, L.wonderVictoryCount.max, L.wonderVictoryCount.def),
    wonderMinutes: clampInt(s.wonderVictoryMinutes, L.wonderVictoryMinutes.min, L.wonderVictoryMinutes.max, L.wonderVictoryMinutes.def),
    hillVictory: on(s.hillVictory, "kingOfTheHill"),
    hillMinutes: clampInt(s.hillVictoryMinutes, L.hillVictoryMinutes.min, L.hillVictoryMinutes.max, L.hillVictoryMinutes.def),
    // Infinite gold makes banking gold meaningless.
    // (The Economic mode switches infinite gold off: resolveSettings.)
    economicVictory: on(s.economicVictory, "economic") && (m === "economic" || !s.infiniteGold),
    economicGold: clampInt(s.economicVictoryGold, L.economicVictoryGold.min, L.economicVictoryGold.max, L.economicVictoryGold.def),
  };
}

/** Any of the extra victory conditions is on. */
export function anyExtraVictory(s: GameSettings): boolean {
  const v = victorySettingsOf(s);
  return v.wonderVictory || v.hillVictory || v.economicVictory;
}

/**
 * Copy the module's settings from untrusted `x` into `out` (validated; absent / invalid fields
 * fall back to `base`, and are left out when `base` has none either).
 */
export function copyVictorySettings(x: Record<string, unknown>, base: GameSettings, out: GameSettings): void {
  const L = VICTORY_LIMITS;
  const bool = (k: "wonders" | "wonderVictory" | "hillVictory" | "economicVictory"): void => {
    const v = typeof x[k] === "boolean" ? (x[k] as boolean) : base[k];
    if (typeof v === "boolean") out[k] = v;
  };
  const int = (k: "wonderVictoryCount" | "wonderVictoryMinutes" | "hillVictoryMinutes" | "economicVictoryGold"): void => {
    const lim = L[k];
    const raw = typeof x[k] === "number" && Number.isFinite(x[k]) ? x[k] : base[k];
    if (typeof raw === "number" && Number.isFinite(raw)) out[k] = clampInt(raw, lim.min, lim.max, lim.def);
  };
  bool("wonders");
  bool("wonderVictory");
  bool("hillVictory");
  bool("economicVictory");
  int("wonderVictoryCount");
  int("wonderVictoryMinutes");
  int("hillVictoryMinutes");
  int("economicVictoryGold");
}

/** The module's validated settings of untrusted `x` as a partial settings object (see copyVictorySettings). */
export function pickVictorySettings(x: Record<string, unknown>, base: GameSettings): Partial<GameSettings> {
  const out = {} as GameSettings;
  copyVictorySettings(x, base, out);
  return out;
}
